import '@/index.css'
import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router'
import { AuthProvider } from '@/components/auth/auth-provider'
import { TokenUpdater } from '@/components/auth/token-updater'
import { GraphQLProvider } from '@/components/graphql/provider'
import { Root } from '@/components/root'
import { SuperRoot } from '@/components/super-root'
import { ThemeProvider } from '@/components/theme/theme-provider'
import { Toaster } from '@/components/ui/sonner'
import { getEnv } from '@/config/get-env'
import { THEME_KEY } from '@/config/local-storage'
import { createApolloClient } from '@/lib/graphql/create-apollo-client'
import { AppPageFallback } from '@/routes/app/fallback'
import { CreateApp } from '@/routes/apps/create-app'
import { Authorize } from '@/routes/authorize'
import { Billing } from '@/routes/billing'
import { DatabasePageFallback } from '@/routes/database/fallback'
import { CreateDatabase } from '@/routes/databases/create-database'
import { Home } from '@/routes/home'
import { LoadBalancerPageFallback } from '@/routes/load-balancer/fallback'
import { CreateLoadBalancer } from '@/routes/load-balancers/create-load-balancer'
import { Login } from '@/routes/login'
import { RequestResetPassword } from '@/routes/login/request-reset-password'
import { ResetPassword } from '@/routes/login/reset-password'
import { SsoLogin } from '@/routes/login/sso'
import { NotFound } from '@/routes/not-found'
import { RegistryCredentials } from '@/routes/registry-credentials'
import { Regtoken } from '@/routes/regtoken'
import { SingleSignOn } from '@/routes/single-sign-on'
import { StaticSitePageFallback } from '@/routes/static-site/fallback'
import { CreateStaticSite } from '@/routes/static-sites/create-static-site'
import { Template } from '@/routes/template'
import { Templates } from '@/routes/templates'
import { Usage } from '@/routes/usage'
import { UserManagement } from '@/routes/user-management'
import { AcceptInvitation } from '@/routes/user-management/accept-invitation'
import Webhook from '@/routes/webhook'
import { WebhookPageFallback } from '@/routes/webhook/fallback'

Sentry.init({
  dsn: 'https://2245e0594c5b393608e9f8b8cc2e9a98@o286090.ingest.us.sentry.io/4510187091197952',
  sendDefaultPii: true,
  environment: getEnv(),
  beforeSend(event, hint) {
    if (
      hint.originalException instanceof DOMException &&
      hint.originalException.name === 'AbortError'
    ) {
      return null
    }
    return event
  },
})

const verificationKey = 'vite-reload-verification'

window.addEventListener('vite:preloadError', () => {
  const isReloading = sessionStorage.getItem(verificationKey)

  if (isReloading) {
    console.error(`Reloading didn't fix the module loading error.`)
    return
  }

  sessionStorage.setItem(verificationKey, 'true')
  window.location.reload()
})

window.addEventListener('load', () => {
  sessionStorage.removeItem(verificationKey)
})

const pApolloClient = (async () => await createApolloClient())()

const Apps = lazy(() => import('@/routes/apps'))
const App = lazy(() => import('@/routes/app'))
const Databases = lazy(() => import('@/routes/databases'))
const Database = lazy(() => import('@/routes/database'))
const StaticSites = lazy(() => import('@/routes/static-sites'))
const StaticSite = lazy(() => import('@/routes/static-site'))
const Pipeline = lazy(() => import('@/routes/pipeline'))
const ObjectStorages = lazy(() => import('@/routes/object-storages'))
const ObjectStorage = lazy(() => import('@/routes/object-storage'))
const Projects = lazy(() => import('@/routes/projects'))
const Project = lazy(() => import('@/routes/project'))
const LoadBalancers = lazy(() => import('@/routes/load-balancers'))
const LoadBalancer = lazy(() => import('@/routes/load-balancer'))
const ApiKeys = lazy(() => import('@/routes/api-keys'))
const AiSupport = lazy(() => import('@/routes/ai-support'))
const Webhooks = lazy(() => import('@/routes/webhooks'))
const GlobalVariables = lazy(() => import('@/routes/global-variables'))
const MyAccount = lazy(() => import('@/routes/my-account'))
const ConfigureSso = lazy(
  () => import('@/routes/single-sign-on/configure-wizard')
)

// Super routes
const SuperCustomerHealthMonitor = lazy(
  () => import('@/super-routes/customer-health-monitor')
)
const SuperSearch = lazy(() => import('@/super-routes/search'))
const SuperUserDetails = lazy(() => import('@/super-routes/user-details'))
const SuperBlockedGit = lazy(() => import('@/super-routes/blocked-git'))
const SuperCompanies = lazy(() => import('@/super-routes/companies'))
const SuperChurns = lazy(() => import('@/super-routes/churns'))
const SuperStats = lazy(() => import('@/super-routes/stats'))
const SuperOciMigration = lazy(() => import('@/super-routes/oci-migration'))
const SuperOciMigrationDetail = lazy(
  () => import('@/super-routes/oci-migration/migration')
)
const SuperOciMigrationSchedule = lazy(
  () => import('@/super-routes/oci-migration-schedule')
)
const SuperOciMigrationPrecheck = lazy(
  () => import('@/super-routes/oci-migration-precheck')
)
const SuperOciMigrationPrecheckRun = lazy(
  () => import('@/super-routes/oci-migration-precheck/run')
)

createRoot(document.getElementById('root')!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack)
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <ThemeProvider defaultTheme="system" storageKey={THEME_KEY}>
      <AuthProvider>
        <GraphQLProvider pApolloClient={pApolloClient}>
          <TokenUpdater />
          <BrowserRouter>
            <Routes>
              <Route path="regtoken" element={<Regtoken />} />
              <Route path="authorize" element={<Authorize />} />
              <Route path="login" element={<Login />} />
              <Route path="login/sso" element={<SsoLogin />} />
              <Route
                path="login/resetpassword/:token"
                element={<ResetPassword />}
              />
              <Route
                path="acceptinvitation/:idInvitation"
                element={<AcceptInvitation />}
              />
              <Route
                path="login/request-reset-password"
                element={<RequestResetPassword />}
              />
              <Route path="superadmin/*" element={<SuperRoot />}>
                <Route index element={<SuperCompanies />} />
                <Route
                  path="customer-health-monitor"
                  element={<SuperCustomerHealthMonitor />}
                />
                <Route
                  path="customer-health-monitor/:idCompany"
                  element={<SuperCustomerHealthMonitor />}
                />
                <Route path="search" element={<SuperSearch />} />
                <Route path="user-details" element={<SuperUserDetails />} />
                <Route
                  path="user-details/:idUser"
                  element={<SuperUserDetails />}
                />
                <Route path="blocked-git" element={<SuperBlockedGit />} />
                <Route path="churns" element={<SuperChurns />} />
                <Route path="stats" element={<SuperStats />} />
                <Route path="oci-migration" element={<SuperOciMigration />} />
                <Route
                  path="oci-migration/:idMigration"
                  element={<SuperOciMigrationDetail />}
                />
                <Route
                  path="oci-migration-schedule"
                  element={<SuperOciMigrationSchedule />}
                />
                <Route
                  path="oci-migration-precheck"
                  element={<SuperOciMigrationPrecheck />}
                />
                <Route
                  path="oci-migration-precheck/:idRun"
                  element={<SuperOciMigrationPrecheckRun />}
                />
                <Route path="companies" element={<SuperCompanies />} />
                <Route
                  path="companies/:idCompany"
                  element={<SuperCompanies />}
                />
              </Route>
              <Route element={<Root />}>
                <Route path="/" element={<Home />} />
                <Route path="/apps" element={<Apps />} />
                <Route path="/apps/create" element={<CreateApp />} />
                <Route path="/apps/create-pipeline" element={<Apps />} />
                <Route
                  path="/app/:appName/*"
                  element={
                    <Suspense fallback={<AppPageFallback />}>
                      <App />
                    </Suspense>
                  }
                />
                <Route path="/pipeline/:idPipeline/*" element={<Pipeline />} />
                <Route
                  path="/pipeline/:idPipeline/update"
                  element={<Pipeline />}
                />
                <Route
                  path="/pipeline/:idPipeline/deploy"
                  element={<Pipeline />}
                />
                <Route
                  path="/pipeline/:idPipeline/enable-preview"
                  element={<Pipeline />}
                />
                <Route
                  path="/pipeline/:idPipeline/disable-preview"
                  element={<Pipeline />}
                />
                <Route
                  path="/pipeline/:idPipeline/update-preview"
                  element={<Pipeline />}
                />
                <Route
                  path="/pipeline/:idPipeline/delete"
                  element={<Pipeline />}
                />
                <Route
                  path="/pipeline/:idPipeline/stage/create"
                  element={<Pipeline />}
                />
                <Route
                  path="/pipeline/:idPipeline/stage/:idStage/delete"
                  element={<Pipeline />}
                />
                <Route
                  path="/pipeline/:idPipeline/stage/:idStage/add-app"
                  element={<Pipeline />}
                />
                <Route
                  path="/pipeline/:idPipeline/stage/:idStage/remove-app/:idApp"
                  element={<Pipeline />}
                />
                <Route path="/databases" element={<Databases />} />
                <Route path="/databases/create" element={<CreateDatabase />} />
                <Route
                  path="/database/:databaseName/*"
                  element={
                    <Suspense fallback={<DatabasePageFallback />}>
                      <Database />
                    </Suspense>
                  }
                />
                <Route path="/static-sites" element={<StaticSites />} />
                <Route
                  path="/static-sites/create"
                  element={<CreateStaticSite />}
                />
                <Route
                  path="/static-site/:staticSiteName/*"
                  element={
                    <Suspense fallback={<StaticSitePageFallback />}>
                      <StaticSite />
                    </Suspense>
                  }
                />
                <Route path="/object-storage" element={<ObjectStorages />}>
                  <Route path="create" element={<ObjectStorages />} />
                </Route>
                <Route
                  path="/object-storage/:idObjectStorage/*"
                  element={<ObjectStorage />}
                />
                <Route path="/load-balancers" element={<LoadBalancers />} />
                <Route
                  path="/load-balancers/create"
                  element={<CreateLoadBalancer />}
                />
                <Route
                  path="/load-balancer/:loadBalancerName/*"
                  element={
                    <Suspense fallback={<LoadBalancerPageFallback />}>
                      <LoadBalancer />
                    </Suspense>
                  }
                />
                <Route path="/projects" element={<Projects />}>
                  <Route path="create" element={<Projects />} />
                </Route>
                <Route path="/project/:projectName/*" element={<Project />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/templates/:name/deploy" element={<Templates />} />
                <Route path="/template/:name" element={<Template />} />
                <Route path="/template/:name/deploy" element={<Template />} />
                <Route path="/billing/*" element={<Billing />} />
                <Route path="/usage/*" element={<Usage />} />

                <Route
                  path="/registry-credentials"
                  element={<RegistryCredentials />}
                />
                <Route
                  path="/registry-credentials/delete/:idRegistryCredential"
                  element={<RegistryCredentials />}
                />
                <Route
                  path="/registry-credentials/create"
                  element={<RegistryCredentials />}
                />
                <Route path="/api-keys/*" element={<ApiKeys />} />
                <Route
                  path="/api-keys/delete/:idApiKey"
                  element={<ApiKeys />}
                />
                <Route
                  path="/api-keys/update/:idApiKey"
                  element={<ApiKeys />}
                />
                <Route
                  path="/api-keys/rotate/:idApiKey"
                  element={<ApiKeys />}
                />
                <Route
                  path="/api-keys/toggle/:idApiKey"
                  element={<ApiKeys />}
                />
                <Route path="/webhooks/*" element={<Webhooks />} />
                <Route
                  path="/webhook/:idWebhook/*"
                  element={
                    <Suspense fallback={<WebhookPageFallback />}>
                      <Webhook />
                    </Suspense>
                  }
                >
                  <Route
                    path="event-deliveries/:idEventDelivery"
                    element={
                      <Suspense fallback={<WebhookPageFallback />}>
                        <Webhook />
                      </Suspense>
                    }
                  />
                </Route>
                <Route path="/global-variables/*" element={<GlobalVariables />}>
                  <Route path="apps/:idEnvironmentVariable/update" />
                </Route>
                <Route path="/user-management" element={<UserManagement />} />
                <Route
                  path="/user-management/invite-users"
                  element={<UserManagement />}
                />
                <Route
                  path="/user-management/cancel-invitation/:idInvitation"
                  element={<UserManagement />}
                />
                <Route
                  path="/user-management/remove-user/:idUser"
                  element={<UserManagement />}
                />
                <Route
                  path="/user-management/update-user-access/:idUser"
                  element={<UserManagement />}
                />
                <Route
                  path="/user-management/transfer-ownership"
                  element={<UserManagement />}
                />
                <Route path="/single-sign-on" element={<SingleSignOn />} />
                <Route
                  path="/single-sign-on/disable"
                  element={<SingleSignOn />}
                />
                <Route
                  path="/single-sign-on/toggle-force-sso"
                  element={<SingleSignOn />}
                />
                <Route
                  path="/single-sign-on/add-exception"
                  element={<SingleSignOn />}
                />
                <Route
                  path="/single-sign-on/remove-exception/:idUser"
                  element={<SingleSignOn />}
                />
                <Route
                  path="/single-sign-on/toggle-jit"
                  element={<SingleSignOn />}
                />
                <Route
                  path="/single-sign-on/change-default-role"
                  element={<SingleSignOn />}
                />
                <Route
                  path="/single-sign-on/configure/:idSamlSetting"
                  element={<ConfigureSso />}
                />
                <Route path="/help/ai-support" element={<AiSupport />} />
                <Route path="/my-account/*" element={<MyAccount />} />
                <Route
                  path="/my-account/revoke-git-login/:gitType"
                  element={<MyAccount />}
                />
                <Route
                  path="/my-account/revoke-git-repository-access/:gitType"
                  element={<MyAccount />}
                />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster position="top-right" />
          </BrowserRouter>
        </GraphQLProvider>
      </AuthProvider>
    </ThemeProvider>
  </StrictMode>
)
